import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import TenantAPI from "../../api/TenantAPI";
import useCloudPermissions from "../../../hooks/useCloudPermissions";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import { newToastEvent } from "../../../utils/ToastUtil";
import { AppConfirm } from "../../../utils/PopupUtil";
import {
  callFunctionIfExists,
  getErrorMessage,
} from "../../../utils/GeneralUtils";

export type operationTypes = "Restore" | "Deletion";

interface DeletedRecordOperations {
  Operation: operationTypes;
  RecordIDs: string[];
  onSuccess?: () => void;
  onError?: () => void;
}

const confirmOperation = async (operation: operationTypes, len: number) => {
  const result = await AppConfirm({
    title: `Confirm Record(s) ${
      operation === "Restore" ? "Restoration" : "Deletion"
    }`,
    text: `Are you sure you want to ${
      operation === "Restore" ? "restore" : "permanently delete"
    } ${len === 1 ? "this record" : "these records"}?`,
    confirmButtonText: operation === "Restore" ? "Restore" : "Delete",
    confirmButtonColor:
      operation === "Restore" ? "var(--primary)" : "var(--danger)",
  });

  return result.isConfirmed;
};

const useDeleteRecordOperations = () => {
  const { cPermissions } = useCloudPermissions();
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  const useDeleteRecordOperation = useMutation({
    mutationFn: ({ Operation, RecordIDs }: DeletedRecordOperations) => {
      if (!cPermissions.cloud_modify_deleted_records) {
        return Promise.reject(
          "You do not have permission to restore/deleted these records in this cloud."
        );
      }

      return TenantAPI.deletedRecordOperations({ Operation, RecordIDs });
    },
    onSuccess: (_, { RecordIDs, Operation, onSuccess }) => {
      queryClient.setQueriesData(
        { queryKey: [tenantID, "records", "DeletedRecords"] },
        (prev: any) => {
          if (!prev?.records) return;

          let newRecords = prev.records.filter((r: any) => {
            if (RecordIDs.includes(r.id)) {
              return false;
            }
            return true;
          });

          return { ...prev, records: newRecords };
        }
      );

      newToastEvent(
        "success",
        `Succesfully ${
          Operation === "Restore" ? "restored" : "deleted"
        } records.`
      );
      callFunctionIfExists(onSuccess);
    },
    onError: (error, { onError }) => {
      console.log({ error });
      newToastEvent(
        "warning",
        getErrorMessage(error) ?? "Something went wrong. Please try again."
      );

      callFunctionIfExists(onError);
    },
  });

  return {
    confirmOperation,
    performOperation: useDeleteRecordOperation.mutate,
    loading: useDeleteRecordOperation.isPending,
    error: useDeleteRecordOperation.error
      ? getErrorMessage(useDeleteRecordOperation.error)
      : null,
  };
};

export default useDeleteRecordOperations;
